import { inject, Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, race, timer } from 'rxjs';
import {
  switchMap,
  catchError,
  map,
  tap,
  debounceTime,
  filter,
  takeUntil,
  concatMap,
  delay,
  take
} from 'rxjs/operators';

import {
  zefWebsocketAuth,
  zefWebsocketSuccess,
  zefWebsocketFail,
  zefWebsocketMessage,
  zefWebsocketClosed,
  zefWebsocketOpened,
  zefWebsocketTerminate
} from './websocket.action';
import { WebsocketApi } from './websocket.api';
import { PING_PONG_ENABLED, PING_PONG_TIMER } from './websocket.constant';
import { log } from '../core';

@Injectable()
export class WebsocketEffect {

  #actions$ = inject(Actions);
  #api = inject(WebsocketApi);
  #pingPongEnabled = inject(PING_PONG_ENABLED);
  #pingPongTimer = inject(PING_PONG_TIMER);

  private _onWebsocketAuth$ = this.#actions$.pipe(ofType(zefWebsocketAuth));

  onAuth$ = createEffect(() => this._onWebsocketAuth$.pipe(
    concatMap((action) => this.#api
      .auth$(action.data.token)
      .pipe(
        map(({ webSocketToken }) => zefWebsocketSuccess(
          {
            token: webSocketToken,
            receiverId: action.data.receiverId
          },
          action
        )),
        catchError((err) => of(zefWebsocketFail(err, action)))
      )
    )
  ));

  onAuthFail$ = createEffect(() => this.#actions$.pipe(
    ofType(zefWebsocketFail),
    log('_onAuthFail$'),
    debounceTime(10000),
    map((action) => action.originalAction)
  ));

  onAuthSuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(zefWebsocketSuccess),
    tap(({ data: { token, receiverId } }) => this.#api.connect(token, receiverId)),
    switchMap(() => this.#api.messages$.pipe(
      takeUntil(this.#actions$.pipe(ofType(zefWebsocketTerminate))),
      map((message) => zefWebsocketMessage(message)),
      catchError((err) => of(zefWebsocketClosed(err)))
    ))
  ));

  onWsOpened$ = createEffect(() => this.#actions$.pipe(
    ofType(zefWebsocketMessage),
    filter((action) => action.message?.type === 'SocketSuccess'),
    map(() => zefWebsocketOpened())
  ));

  onStartPingPong$ = createEffect(() => this.#actions$.pipe(
    // upon connecting to websocket for
    // the first time, start a 5000ms timer
    ofType(zefWebsocketOpened),
    filter(() => this.#pingPongEnabled),
    switchMap(() => timer(0, this.#pingPongTimer).pipe(
      // on each timer tick, race between
      // a 3000ms timeout and a websocket
      // message that includes 'pong' response
      switchMap(() => race(
        of('timeout').pipe(delay(3000)),
        // empty observable to wrap sync 'send'
        // method and a 'pong' message stream,
        // of which we only want the first result
        of(undefined).pipe(
          tap(() => this.#api.send({ type: 'ping' })),
          switchMap(() => this.#actions$.pipe(
            ofType(zefWebsocketMessage),
            filter((action) => action.message?.type === 'pong'),
            take(1)
          ))
        )
      )),
      takeUntil(this.#actions$.pipe(ofType(zefWebsocketClosed), take(1)))
    )),
    // if 3000 timeout finishes first
    // trigger websocket closed action
    // which in turn will cause new
    // auth, new zefWebsocketOpened
    // and a new timer
    filter((d) => d === 'timeout'),
    map(() => zefWebsocketClosed())
  ))

}
